// Mirrors PRI.Shared.Models.TaxonomyConfig.IsPolicyViolationType: a root classification is a
// policy violation if its `type` is the current key or the pre-rename legacy key. Existing rows
// (and anything read before the compatibility view is dropped) can still carry `vertical`.
export function isPolicyViolationType(type: 'policy_violation' | 'vertical' | 'risk'): boolean {
  return type === 'policy_violation' || type === 'vertical';
}

// A node is visible for a facet when the facet is in its categories and the node does not hide it;
// with no facet, when any of its categories is not hidden. Callers walking a tree apply this to
// every node on the path, so a hidden ancestor hides everything beneath it.
export function isVisibleForFacet(
  classification: {categories: readonly string[]; hiddenFacets: readonly string[]},
  facet?: 'Actor' | 'Content'
): boolean {
  const {categories, hiddenFacets} = classification;
  return facet
    ? categories.includes(facet) && !hiddenFacets.includes(facet)
    : categories.some((category) => !hiddenFacets.includes(category));
}

type TreeNode = {
  id: string;
  parentId: string | null;
  name: string;
  type: 'policy_violation' | 'vertical' | 'risk';
  conceptId?: string | null;
};

// The nodes in `roots` and every node beneath them, in the input's order.
function subtree<T extends TreeNode>(all: readonly T[], roots: readonly T[]): T[] {
  const included = new Set(roots.map((node) => node.id));
  let grew = included.size > 0;
  while (grew) {
    grew = false;
    for (const node of all) {
      if (!included.has(node.id) && node.parentId !== null && included.has(node.parentId)) {
        included.add(node.id);
        grew = true;
      }
    }
  }

  return all.filter((node) => included.has(node.id));
}

// The policy violation named `policyViolation` and every node beneath it, in the input's order.
export function classificationsUnder<T extends TreeNode>(
  all: readonly T[],
  policyViolation: string
): T[] {
  return subtree(
    all,
    all.filter((node) => isPolicyViolationType(node.type) && node.name === policyViolation)
  );
}

// The node under `policyViolation` standing for the engine's suggested concept, if there is one.
export function suggestedClassification<T extends TreeNode>(
  all: readonly T[],
  policyViolation: string,
  conceptId: string | undefined
): T | undefined {
  return conceptId
    ? classificationsUnder(all, policyViolation).find(
        (node) => !isPolicyViolationType(node.type) && node.conceptId === conceptId
      )
    : undefined;
}

// `node`, its ancestors up to the policy violation and everything beneath it, in the input's order.
export function classificationLineage<T extends TreeNode>(all: readonly T[], node: T): T[] {
  const ancestors = new Set<string>();
  for (
    let parent = all.find((other) => other.id === node.parentId);
    parent;
    parent = all.find((other) => other.id === parent?.parentId)
  ) {
    ancestors.add(parent.id);
  }

  const below = new Set(subtree(all, [node]).map((other) => other.id));
  return all.filter((other) => ancestors.has(other.id) || below.has(other.id));
}
